import { useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { dateFormat, numberFormat } from 'shared/util'
import { Article as ArticleIcon } from '@mui/icons-material'

import {
  Card,
  CardMedia,
  CardActionArea,
  CardContent,
  Box,
  Typography,
  Skeleton,
  Stack,
  Divider,
  Chip
} from '@mui/material'

import { ValueSpan } from 'components/controls/valueSpan'

import { useGetSearchBunner } from 'interface/search'
import { SearchResultWithBunner } from 'features/search/types'
import { SearchStatusChip } from './searchStatusChip'

type SearchRecordProps = {
  logo: string | undefined
  record: SearchResultWithBunner
  onSelectSearch: (searchId: number) => void
}
export const SearchRecord = ({
  logo,
  record,
  onSelectSearch
}: SearchRecordProps) => {
  const { ref: refInView, inView } = useInView({ triggerOnce: true })

  const bunnerState = useGetSearchBunner(inView ? record.searchId : undefined, { updateTime: record.updateTime })
  const bunnerImage = useMemo(
    () => bunnerState.data ? URL.createObjectURL(bunnerState.data.blob) : undefined,
    [bunnerState]
  )
  return (
    <Card ref={refInView} sx={{ position: 'relative' }}>
      <CardActionArea onClick={() => onSelectSearch(record.searchId)}>
        <Box position="relative" minHeight={128}>
          <Stack
            direction="column"
            alignItems="flex-end"
            spacing={0.5}
            sx={{ position: 'absolute', right: 0, top: 0, margin: 1 }}
          >
            {record.targetListCount > 0 ? (
              <Chip
                icon={<ArticleIcon/>}
                label={numberFormat(record.targetListCount) + " 件"}
                color="info"
                size="small"
              />
            ) : <></>}
          </Stack>
          {logo ? (
            <img
              alt={record.title}
              src={logo}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                margin: '8px',
                borderRadius: 1,
                width: '100px',
              }}
            />
          ) : (
            <></>
          )}
          {bunnerImage ? (
            <CardMedia component="img" height="128" image={bunnerImage} />
          ) : (
            <Skeleton variant="rectangular" animation="wave" height={128} />
          )}
        </Box>

        <CardContent>
          <Typography
            variant="h5"
            color="text.primary"
            textAlign="center"
            sx={{ wordBreak: "break-all" }}
            component="div"
          >
            {record.title}
          </Typography>

          <Typography
            variant="caption"
            color="text.primary"
            textAlign="center"
            component="div"
          >
            {dateFormat(record.createTime)}
          </Typography>
        </CardContent>

        {((record.sisetuCount ?? 0) + (record.kojinCount ?? 0)) > 0 ? (
          <>
            <Divider/>

            <Stack direction="row" padding={0.5} alignItems="center">
              <SearchStatusChip status={record.status} size="small"/>
              <Stack
                direction="row"
                flexGrow={1}
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                {(record.sisetuCount ?? 0) > 0 && <ValueSpan text={numberFormat(record.sisetuCount)} suffix="施設"/>}
                {(record.kojinCount ?? 0) > 0 && <ValueSpan text={numberFormat(record.kojinCount)} suffix="人"/>}
              </Stack>
            </Stack>
          </>
        ) : <></> }
      </CardActionArea>
    </Card>
  )
}
